import { useTheme } from "@mui/material";

import { CustomersStepTypeEnumPaths, ICustomersInsuranceManualResponse, ICustomersInsuranceTestClaimPrescription } from "@/shared/endpoints/customers";
import { useAppSelector } from "@/shared/hooks/store";
import { selectOnboardingUser } from "@/store/slices/onboarding/selectors";
import { IPrescribables } from "@/shared/helpers/main";
import CreditCardGreenIcon from '@/assets/icons/credit-card-green.png';
import { Alert, Button } from "@/shared/ui";
import { HueOption } from "@/modules/onboarding/constants";
import { URLSearchParamsInit, useNavigate } from "react-router-dom";
import { ROUTES } from "@/shared/constants/routes";
import { useMemo } from "react";
import _ from "lodash";

import './style.css';

interface IProps {
    claims: Record<string, ICustomersInsuranceTestClaimPrescription>;
    showCopayLoading: boolean;
    onShowCopay?(): void;
    eligibleInsurances: ICustomersInsuranceManualResponse[];
    navigateToStep?: (step: CustomersStepTypeEnumPaths, index?: number, searchParams?: URLSearchParamsInit) => void;
    copayRequestsExhausted?: boolean;
    prescribables: IPrescribables;
}

const ReviewMainInfo = ({
    claims,
    showCopayLoading,
    onShowCopay,
    eligibleInsurances,
    navigateToStep,
    copayRequestsExhausted = false,
    prescribables = { includingMedbox: [], packagedSeparately: [] }
}: IProps) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const user = useAppSelector(selectOnboardingUser);

    const hasMissingCopayAmount = useMemo(() => {
        const allItems = [...prescribables.includingMedbox, ...prescribables.packagedSeparately];
        const otcFalse = allItems.filter(item => item.otc === false).length;
        return !_.isEmpty(claims) && (Object.values(claims).some(item => item.coPay === null) || Object.values(claims).length < otcFalse)
    }, [claims, prescribables]);

    const selectedHue = useMemo(() => (
        user && HueOption.find(hue => hue.id === user.userOnboarding?.details?.medboxHueType)
    ), [user]);

    const onHueColorChange = () => {
        if (navigateToStep) navigateToStep(CustomersStepTypeEnumPaths.PRESCRIPTIONS, -1, { fromReview: '1' });
        else {
            navigate(
                `/${ROUTES.ONBOARDING.MAIN}/${ROUTES.ONBOARDING.PRESCRIPTIONS.MAIN}/${ROUTES.ONBOARDING.PRESCRIPTIONS.MEDBOX_HUE}?from=review`
            );
        }
    };


    const AlertUi = useMemo(() => {
        if (copayRequestsExhausted) {
            return (
                <Alert
                    variant="warning"
                    className="mt-[1.625rem]"
                    label={
                        <div className="flex flex-col items-start">
                            <span className="leading-[137%] text-15 font-atkinsonHyperlegible font-normal md:text-16">
                                <span className="font-atkinsonBold font-bold">It looks like you’ve reach the maximum usage limit for showing copays.</span>
                                &nbsp;Still have questions? Our support team is available to assist.
                            </span>

                            <Button
                                variant="text"
                                sx={{ mt: '0.625rem' }}
                                className='intercom-launcher'
                            >
                                <span className="font-dmSansMedium font-bold text-17 md:text-lg">Chat Support</span>
                            </Button>
                        </div>

                    }
                />
            )
        } else if (hasMissingCopayAmount) {
            return (
                <Alert
                    variant="warning"
                    className="mt-[1.625rem]"
                    label={
                        <span className="font-atkinsonHyperlegible font-normal">
                            Some copays aren’t available yet due to the item type (inhalers or creams) or the timing of your next refill.
                            &nbsp;
                            <span className="font-atkinsonBold">We’ll update your copays once your insurance provides it.</span>
                        </span>
                    }
                />
            )
        }
        return null;
    }, [copayRequestsExhausted, hasMissingCopayAmount])

    if (!user) return null;

    return (
        <div className="flex flex-col border-b-2 border-mint-200 pb-7 md:items-start">
            <div className="w-full flex justify-between">
                <div className="w-36 sm:w-64">
                    <h4 className="pb-5 font-atkinsonHyperlegible text-lg sm:text-2xl lg:text-3xl">
                        {`${user?.userProfile?.firstName} ${user?.userProfile?.lastName}`}’s MedBox
                    </h4>
                    {user.addresses?.length && user.addresses[0].fullAddress && (
                        <div className="flex flex-col gap-1">
                            <span className="font-atkinsonBold text-sm uppercase md:text-base">Shipping Address:</span>
                            <div className="flex-col font-atkinsonHyperlegible text-sm md:text-[21px] md:leading-[1.875rem]">
                                <p>{`${user.addresses[0].addressLine1} ${user.addresses[0].addressLine2}`}</p>
                                {user.addresses[0].unit && <p>{user.addresses[0].unit}</p>}
                                <p>{`${user.addresses[0].city}, ${user.addresses[0].state}`}</p>
                                <p>{`${user.addresses[0].zip}`}</p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="box-border h-max w-[174px] overflow-hidden rounded-10 ms-max:w-[120px] md-max:w-[138px]">
                    <div className="relative h-[150px] overflow-hidden md-max:h-[100px]">
                        <Button
                            className="review__edit_color"
                            color="primary"
                            size="small"
                            sx={{
                                padding: '1px 10px !important',
                                border: 'inherit',
                                background: '#003D3380',
                                fontFamily: 'Atkinson',
                                position: 'absolute',
                                right: '7px',
                                top: '7px',
                                zIndex: 3,
                                fontSize: '15px',
                                [theme.breakpoints.down('md')]: {
                                    padding: '1px 9px !important',
                                    fontSize: '14px'
                                },
                                ':hover': {
                                    bgcolor: 'primary.700'
                                }
                            }}
                            onClick={onHueColorChange}
                        >
                            Edit Color
                        </Button>
                        <img src={selectedHue?.src} className="relative object-center" />
                    </div>
                    <div className="flex items-center justify-center bg-primary-700 py-1 font-dmSans text-base text-white md:text-lg">
                        {selectedHue?.name}
                    </div>
                </div>
            </div>

            {eligibleInsurances.length === 1 && _.isEmpty(claims) && (
                <Button
                    loading={showCopayLoading}
                    className="show-my-copays-button mt-[1.625rem] md:min-w-[18.125rem]"
                    variant="outlined"
                    onClick={onShowCopay}
                >
                    <span className="font-bold flex justify-center items-center text-base">
                        <img
                            width={15}
                            className="md:w-[1.3125rem] w-[0.9375rem] mr-[0.5625rem]"
                            src={CreditCardGreenIcon}
                            alt="credit card green"
                        />

                        Show My Copays
                    </span>
                </Button>
            )}
            {AlertUi}
        </div>
    );
}

export default ReviewMainInfo;